// bluaIQ — Gantt color system (FE-only, centralized)
//
// Honest dynamic legend: the color shown for a phase in the legend is the EXACT
// color the bars use (both call phaseColor), and the legend lists ONLY phases
// actually present on the chart (buildPhaseLegend reads the visible tasks). No
// task-text guessing, no Takeoff-palette dependency, no decorative/unused colors.
//
// Bars are filled by their real phase; critical/blocked/today are overlays that
// layer ON TOP of the fill (red outline/icon, amber stripe/badge, red today
// line) — they never replace the phase fill. Status badges stay separate.
//
// SEMANTIC_STATUS_COLORS documents the cross-module status palette so Quantity
// Takeoff / Cuantificación de Obra can reuse the SAME meanings later. This
// module deliberately has NO Takeoff import — alignment happens when Takeoff is
// active. See memory: design-semantic-colors.

import { phaseRank, phaseDisplayLabel } from './ganttPhaseOrder'

// Reserved status/overlay colors — these are NEVER used as a phase fill, so a
// phase bar can never be mistaken for the critical/blocked/today treatments.
// Shared semantic meanings for future cross-module reuse (do not fork per
// module). This PR consumes only critical/blocked/today.
export const SEMANTIC_STATUS_COLORS = {
  critical: '#EF4444',      // red — critical-path outline + ⚠ icon + today line
  blockedBorder: '#D97706', // amber-600 — blocked bar border
  blockedStripe: '#FDE68A', // amber-200 — blocked bar diagonal stripe
  blocked: '#D97706',
  pending: '#9E9890',       // neutral
  inProgress: '#0EA5E9',    // sky
  completed: '#10B981',     // emerald
  warning: '#F59E0B',       // amber
  required: '#D97706',      // required/missing dependency (blocker)
} as const

export const CRITICAL_COLOR = SEMANTIC_STATUS_COLORS.critical
export const TODAY_COLOR = SEMANTIC_STATUS_COLORS.critical

// Phase-fill palette — distinct, accessible hues that EXCLUDE the reserved red
// (#EF4444) and amber/orange (#D97706 / #F59E0B / #FDE68A) so no phase fill can
// be confused with the critical/blocked/today treatments.
const PHASE_PALETTE = [
  '#06B6D4', // cyan
  '#3B82F6', // blue
  '#14B8A6', // teal
  '#6366F1', // indigo
  '#10B981', // emerald
  '#8B5CF6', // violet
  '#0EA5E9', // sky
  '#0D9488', // deep teal
  '#A855F7', // purple
  '#2563EB', // royal blue
  '#0891B2', // dark cyan
  '#7C3AED', // deep violet
  '#22C55E', // green
  '#EC4899', // magenta (distinct from reserved red)
] as const

// Stable colors for every phase string the app actually produces today:
//   • default seed → numeric "0".."4"
//   • Auto-Generate Classic → descriptive phase names
//   • jurisdiction packs → PHASE_LABELS English labels (lifecycle.ts)
//   • demo project → descriptive phase names
// Shared names (Foundation, Drywall, …) intentionally share one color across
// sources. Chosen so that within ANY single project the phases are all
// distinct. Unknown/future phases fall back to a deterministic palette hash.
const KNOWN_PHASE_COLOR: Record<string, string> = {
  // Coarse numeric seed phases (Land / Permits / Contracts / Construction / Closeout)
  '0': '#06B6D4',
  '1': '#3B82F6',
  '2': '#8B5CF6',
  '3': '#14B8A6',
  '4': '#10B981',
  // Auto-Generate Classic descriptive preconstruction + closeout stages. Their
  // colors are deliberately OUTSIDE the construction-trade hues below so all 18
  // Classic phase groups are distinct within one schedule (and none is reserved
  // red/amber). Construction trade phases keep their existing colors.
  'Land / Due Diligence': '#78716C',          // stone — land/feasibility
  'Plans & Permits': '#0284C7',               // strong blue — blueprints/permits
  'Bids, Contracts & Insurance': '#BE185D',   // rose — contracts/insurance
  'Closeout / Sale': '#4D7C0F',               // olive green — closing/handover
  // Pre-construction / site
  'Site Work': '#06B6D4',
  'Site': '#06B6D4',
  'Pre-Construction': '#06B6D4',
  'Temporary Power': '#2563EB',
  'Under Slab': '#0891B2',
  // Structure
  'Foundation': '#3B82F6',
  'Framing': '#14B8A6',
  'Frame Rough': '#14B8A6',
  'Roofing': '#EC4899',
  // MEP
  'MEP Rough-In': '#6366F1',
  'MEP': '#6366F1',
  'MEP Trim': '#7C3AED',
  // Envelope / finishes
  'Insulation': '#A855F7',
  'Drywall': '#22C55E',
  'Interior Paint': '#8B5CF6',
  'Finishes': '#8B5CF6',
  'Flooring': '#0891B2',
  'Cabinets & Counters': '#0D9488',
  'Exterior': '#10B981',
  'Landscaping': '#0EA5E9',
  // Utilities / closeout
  'Meter Release': '#7C3AED',
  'Utility': '#0D9488',
  'Final': '#0EA5E9',
  'Final Inspection': '#2563EB',
  'Closeout': '#2563EB',
}

function hashIndex(key: string, mod: number): number {
  let h = 0
  for (let i = 0; i < key.length; i++) h = (h * 31 + key.charCodeAt(i)) >>> 0
  return h % mod
}

/**
 * Deterministic fill color for a Gantt task's RAW phase string. Known phases
 * (numeric seed, Classic, jurisdiction, demo) get curated, within-project
 * distinct colors; any unknown/future phase gets a stable palette color by
 * hash. Never returns a reserved status color (#EF4444 red / amber), so a phase
 * fill never mimics the critical/blocked/today treatments. Never blank.
 */
export function phaseColor(phase: string | null | undefined): string {
  const key = (phase ?? '').trim()
  if (!key) return PHASE_PALETTE[0]
  if (KNOWN_PHASE_COLOR[key]) return KNOWN_PHASE_COLOR[key]
  return PHASE_PALETTE[hashIndex(key, PHASE_PALETTE.length)]
}

export type PhaseLegendItem = { phase: string; color: string; label: string }

/**
 * Build the legend from the phases ACTUALLY present in the given tasks: distinct
 * phases only, ordered by construction-sequence rank (matching the chart's phase
 * grouping), labeled bilingually via phaseDisplayLabel. Each item's color is
 * phaseColor(phase) — identical to the bar fill — so the legend can never show a
 * phase/color that is not on the chart. Empty input → empty legend.
 */
export function buildPhaseLegend(
  tasks: ReadonlyArray<{ phase: string; phaseEs?: string | null }>,
  isEs: boolean,
): PhaseLegendItem[] {
  const firstByPhase = new Map<string, { phaseEs: string | null | undefined; index: number }>()
  let order = 0
  for (const t of tasks) {
    const key = (t.phase ?? '').trim()
    if (!key) continue
    if (!firstByPhase.has(key)) firstByPhase.set(key, { phaseEs: t.phaseEs, index: order++ })
  }
  return Array.from(firstByPhase.entries())
    // Match the chart's phase ordering exactly: rank first, first-seen as tiebreak.
    .sort((a, b) => (phaseRank(a[0]) - phaseRank(b[0])) || (a[1].index - b[1].index))
    .map(([phase, meta]) => ({
      phase,
      color: phaseColor(phase),
      label: phaseDisplayLabel(phase, meta.phaseEs, isEs),
    }))
}
